import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { PATH_STRUCTURE_ASSIGNMENTS } from "@/constants";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { StructureAssignmentsForm } from "@/modules/structure-assignments/components/StructureAssignmentsForm";
import { useStructureAssignmentsBreadcrumbs } from "@/modules/structure-assignments/hooks/useStructureAssignmentsBreadcrumbs";
import { useStructureAssignmentsParams } from "@/modules/structure-assignments/hooks/useStructureAssignmentsParams";
import { useWriteStructureAssignmentsMutation } from "@/modules/structure-assignments/hooks/useWriteStructureAssignmentsMutation";
import { StructureAssignmentsService } from "@/modules/structure-assignments/services/structureAssignments";
import { writeStructureAssignment } from "@/schemas/structure-assignment";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const StructureAssignmentsEditor = memo(() => {
  const navigate = useNavigate();
  const { can_update } = useModulePermissions();
  const { supervisorId, assignmentType } = useStructureAssignmentsParams();
  const { t } = useTranslation();

  const queryFn = () =>
    StructureAssignmentsService.get(supervisorId, assignmentType);

  const assignmentQuery = useQuery({
    queryKey: ["structureAssignment", supervisorId, assignmentType],
    enabled: !!supervisorId && !!assignmentType,
    queryFn,
  });

  const onDeleteSuccess = useCallback(() => {
    navigate(
      generatePath(PATH_STRUCTURE_ASSIGNMENTS, {
        assignmentType,
      }),
    );
  }, [assignmentType, navigate]);

  const updateMutation = useWriteStructureAssignmentsMutation();
  const deleteMutation = useWriteStructureAssignmentsMutation(onDeleteSuccess);
  const breadcrumbItems = useStructureAssignmentsBreadcrumbs([
    {
      label: t("structureAssignments.breadcrumb.edit"),
    },
  ]);

  const onDelete = useCallback(async () => {
    const data = assignmentQuery?.data;
    if (!data) {
      return;
    }
    // We are deleting the assignment by clearing the subordinates array
    // and calling the structure assignment endpoint with PUT method. That's it.
    await deleteMutation.mutateAsync({
      ...data,
      subordinates: [],
    });
  }, [assignmentQuery, deleteMutation]);

  if (assignmentQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <StructureAssignmentsForm
        mutation={updateMutation}
        schema={writeStructureAssignment}
        query={assignmentQuery}
        disabled={!can_update}
        onDelete={onDelete}
      />
    </>
  );
});
